import type { ReactNode } from 'react'

import type { Operator, UserProps } from '@img-arena/consoles-api-dde'
import { DataCardDetailsEntry } from '@/components/card'
import { LicenseFormButton } from '@/components/forms/LicenseForm'
import { UserFormButton } from '@/components/forms/UserForm'
import { ImportUsersCSVButton } from '@/components/ImportUsersCSVButton'
import OperatorMediaFeedDelay from '@/components/OperatorMediaFeedDelay'
import {
  CheckboxDeleteSelectedDDEUserItem,
  CheckboxExportSelectedToCsvItem,
  CheckboxMenu,
  CheckboxSuspendSelectedDDEOperatorLicenseItem
} from '@/components/table/checkbox'

import { LICENSE_STATUSES, LicenseRow } from './Licenses/licenses.dde.table'
import { DDE_OPERATOR_LICENSES_TABLE_ID } from './Licenses/LicensesView.dde'
import { CSV_HEADERS, formatCsvRow } from './Users/users.dde.table'
import { DDE_OPERATOR_USERS_TABLE_ID } from './Users/UsersView.dde'

export const OPERATOR_TABS = {
  LICENSES: 'licenses',
  USERS: 'users'
} as const

export type OperatorTabType = (typeof OPERATOR_TABS)[keyof typeof OPERATOR_TABS]

export const createOperatorTabs = (licensesCount: number, usersCount: number) => [
  { value: OPERATOR_TABS.LICENSES, label: `Licenses (${licensesCount})` },
  { value: OPERATOR_TABS.USERS, label: `Users (${usersCount})` }
]

export const getOperatorHeaderItems = (
  operator: Operator | null | undefined
): DataCardDetailsEntry[] => {
  return [
    {
      key: 'ID',
      value: operator?.id || '-',
      copyable: true
    },
    {
      key: 'Email',
      value: operator?.email || '-'
    },
    {
      key: 'Created Date',
      value: operator?.createdDate || '-'
    },
    {
      key: 'Delay',
      value: `${operator?.delay || 5} sec`
    }
  ]
}

interface HeaderActionsParams {
  tab: string
  operatorId: string
  handleDeleteSelectedUsers: (users: UserProps[]) => void
  handleSuspendSelectedLicenses: (licenses: LicenseRow[]) => void
  fetchOperatorUsers: (params: { operatorId: number }) => void
  fetchOperatorLicenses: (params: { operatorId: number }) => void
}

export const getHeaderPostfixActions = ({
  tab,
  operatorId,
  handleDeleteSelectedUsers,
  handleSuspendSelectedLicenses,
  fetchOperatorUsers,
  fetchOperatorLicenses
}: HeaderActionsParams): ReactNode => {
  if (!operatorId) return null
  const numericOperatorId = Number(operatorId)

  if (tab === OPERATOR_TABS.USERS) {
    return (
      <>
        <CheckboxMenu tableId={DDE_OPERATOR_USERS_TABLE_ID}>
          <CheckboxDeleteSelectedDDEUserItem onDelete={handleDeleteSelectedUsers} />
          <CheckboxExportSelectedToCsvItem
            csvFileName={`operator_${numericOperatorId}_users`}
            csvHeaders={CSV_HEADERS}
            formatCsvRow={formatCsvRow}
          />
        </CheckboxMenu>
        <OperatorMediaFeedDelay operatorId={operatorId} />
        <ImportUsersCSVButton
          operatorId={numericOperatorId}
          onSuccess={() => fetchOperatorUsers({ operatorId: numericOperatorId })}
        />
        <UserFormButton.DDE
          onSubmit={() => fetchOperatorUsers({ operatorId: numericOperatorId })}
          defaultValues={{
            userId: '',
            userName: '',
            role: { operatorId: numericOperatorId, roleName: 'Operator' }
          }}
          disabledProperties={['operator', 'role']}
        />
      </>
    )
  }

  if (tab === OPERATOR_TABS.LICENSES) {
    return (
      <>
        <CheckboxMenu tableId={DDE_OPERATOR_LICENSES_TABLE_ID}>
          <CheckboxSuspendSelectedDDEOperatorLicenseItem
            onSuspend={(licenses) => {
              const licensesToSuspend = licenses.filter(
                (license) => license.status.type !== LICENSE_STATUSES.SUSPENDED
              )
              return handleSuspendSelectedLicenses(licensesToSuspend)
            }}
          />
        </CheckboxMenu>
        <OperatorMediaFeedDelay operatorId={operatorId} />
        <LicenseFormButton.DDE
          data={{ operatorId: numericOperatorId }}
          onSubmit={() => fetchOperatorLicenses({ operatorId: numericOperatorId })}
        />
      </>
    )
  }

  return null
}
